import {
  AllEPriceTime,
  DateType,
  getAllDates,
  getDefaultTime,
  getEPriceTimesByDay,
  RankMethodType,
} from '@/pages/EnergyAnalysisRanking/utils';
import {
  Button,
  DatePicker,
  EchartsWithTheme,
  Empty,
  Form,
  message,
  Modal,
  Radio,
  Select,
  Themes,
  useTheme,
} from '@maxtropy/components';
import { Spin } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyUnitRankingsDetailPost,
  apiV2HighEnergyDeviceElectricityPricePost,
} from '@maxtropy/device-customer-apis-v2';
import { getModalChartOption } from './chart';
import { DataPeriodListItem, getDataQueryPeriodList } from '@/api/energyOverview';
import ElectricCard from './ElectricCard';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { keepPercentOrLine, keepTwoNull } from '@/utils/utils';
import { isNil } from 'lodash-es';
import { CustomerRequest } from '../..';
import { RangeValue } from '../CustomModal';

const RangePicker = DatePicker.RangePicker;

export interface IShowDetailModal {
  id?: number;
  onCancel?: () => void;
  customerRequest?: CustomerRequest;
  timeResolution?: DateType;
  selectedConsumption?: number;
  energyUnitGroupId?: number;
  displayValue?: dayjs.Dayjs;
  dataQueryPeriodId?: number;
  dataQueryPeriodName?: string;
}

const ShowDetailModal: FC<IShowDetailModal> = props => {
  const {
    id,
    onCancel,
    timeResolution,
    customerRequest,
    selectedConsumption,
    energyUnitGroupId,
    displayValue: outDisplayValue,
    dataQueryPeriodId: outDataQueryPeriodId,
    dataQueryPeriodName: outDataQueryPeriodName,
  } = props;
  const theme = useTheme();
  const [displayValue, setDisplayValue] = useState<Dayjs>(dayjs());
  const [displayValueRange, setDisplayValueRange] = useState<RangeValue>();
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataQueryPeriodName, setDataQueryPeriodName] = useState<string>();
  const [dataPeriodList, setDataPeriodList] = useState<DataPeriodListItem[]>([]);

  const [radioValue, setRadioValue] = useState<RankMethodType>(RankMethodType.ALL);
  const chartRef = useRef<EchartsWithThemeRef>();

  useEffect(() => {
    if (timeResolution === DateType.CUSTOM) {
      setDisplayValueRange(customerRequest?.currentPeriod);
    } else {
      setDisplayValue(outDisplayValue!);
    }
    if (timeResolution !== DateType.DAY) {
      setDataQueryPeriodId(outDataQueryPeriodId);
      setDataQueryPeriodName(outDataQueryPeriodName);
    }
  }, []);

  useEffect(() => {
    let startTime: number | undefined;
    let endTime: number | undefined;
    if (timeResolution === DateType.CUSTOM) {
      startTime = getDefaultTime(customerRequest!.timeResolution, customerRequest!.currentPeriod?.[0]).startTime;
      endTime = getDefaultTime(customerRequest!.timeResolution, customerRequest!.currentPeriod?.[1]).endTime;
    } else {
      startTime = getDefaultTime(timeResolution!, displayValue!).startTime;
      endTime = getDefaultTime(timeResolution!, displayValue!).endTime;
    }
    if (startTime && endTime) {
      getDataQueryPeriodList({
        startTime,
        endTime,
      }).then(res => {
        setDataPeriodList(res.dataPeriodList ?? []);
      });
    }
  }, [timeResolution, displayValue, displayValueRange]);

  // 获取图表
  const { data, loading } = useRequest(
    () =>
      apiV2EnergyUnitRankingsDetailPost({
        energyUnitId: id,
        energyUnitGroupId,
        fromTime:
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![0]).startTime
            : getDefaultTime(timeResolution!, displayValue).startTime,
        toTime:
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![1]).endTime
            : getDefaultTime(timeResolution!, displayValue).endTime,
        energyMediumId: selectedConsumption,
        periodIds: dataQueryPeriodId ? [dataQueryPeriodId] : undefined,
        resolutionType:
          timeResolution === DateType.DAY
            ? 'HOUR_QUARTER'
            : timeResolution === DateType.CUSTOM
            ? customerRequest!.timeResolution
            : timeResolution === DateType.YEAR
            ? 'MONTH'
            : ('DAY' as any),
      }).then(res => {
        return res;
      }),

    {
      ready:
        !!id &&
        !!radioValue &&
        !!selectedConsumption &&
        !!energyUnitGroupId &&
        ((timeResolution !== DateType.CUSTOM && !!displayValue) ||
          (timeResolution === DateType.CUSTOM && !!displayValueRange)),
      refreshDeps: [
        id,
        displayValue,
        timeResolution,
        radioValue,
        selectedConsumption,
        dataQueryPeriodId,
        energyUnitGroupId,
        displayValueRange,
        customerRequest,
      ],
    }
  );

  // 获取电价区间
  const { data: ePriceTimes } = useRequest(
    () =>
      apiV2HighEnergyDeviceElectricityPricePost({
        energyUnitGroupId: energyUnitGroupId,
        startTime:
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![0]).startTime
            : getDefaultTime(timeResolution!, displayValue).startTime,
        endTime:
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![1]).endTime
            : getDefaultTime(timeResolution!, displayValue).endTime,
      }).then(res => {
        const dates = getAllDates(
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![0]).startTime!
            : getDefaultTime(timeResolution!, displayValue).startTime!,
          timeResolution === DateType.CUSTOM
            ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![1]).endTime!
            : getDefaultTime(timeResolution!, displayValue).endTime!
        );
        return getEPriceTimesByDay(dates, (res.priceIntervals ?? {}) as unknown as AllEPriceTime);
      }),
    {
      ready: (!!displayValue || !!displayValueRange) && !!energyUnitGroupId,
      refreshDeps: [displayValue, displayValueRange, customerRequest, timeResolution, energyUnitGroupId],
    }
  );

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === DateType.CUSTOM) {
      if (customerRequest?.timeResolution === DateType.MONTH) {
        return 'month';
      } else if (customerRequest?.timeResolution === DateType.WEEK) {
        return 'week';
      } else if (customerRequest?.timeResolution === DateType.YEAR) {
        return 'year';
      } else {
        return 'date';
      }
    } else {
      if (timeResolution === DateType.MONTH) {
        return 'month';
      } else if (timeResolution === DateType.WEEK) {
        return 'week';
      } else if (timeResolution === DateType.YEAR) {
        return 'year';
      } else {
        return 'date';
      }
    }
  }, [timeResolution]);

  // x轴
  const xLabel = useMemo(() => {
    if (
      timeResolution === DateType.MONTH ||
      timeResolution === DateType.WEEK ||
      customerRequest?.timeResolution === DateType.DAY
    ) {
      return 'YYYY-MM-DD';
    } else if (
      timeResolution === DateType.YEAR ||
      customerRequest?.timeResolution === DateType.MONTH ||
      customerRequest?.timeResolution === DateType.WEEK
    ) {
      return 'YYYY-MM';
    } else if (customerRequest?.timeResolution === DateType.YEAR) {
      return 'YYYY';
    } else {
      return 'YYYY-MM-DD HH:mm';
    }
  }, [timeResolution]);

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  const onChangePicker = (dates: RangeValue) => {
    let isFlag = true;
    switch (timeResolution) {
      case DateType.DAY:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
          isFlag = false;
          message.error('最多选择31天');
        }
        return;
      case DateType.WEEK:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(10, 'week').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择10周');
          isFlag = false;
        }
        return;
      case DateType.MONTH:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(24, 'month').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择24个月');
          isFlag = false;
        }
        return;
      case DateType.YEAR:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择6年');
          isFlag = false;
        }
        return;
    }

    setDisplayValueRange(dates);
  };

  // 查询图表
  const chartOption = useMemo(() => {
    if (data) {
      return getModalChartOption({
        xLabel,
        timeResolution,
        chartData: data,
        ePriceTimes,
        dataQueryPeriodName,
        query: {
          startTime:
            timeResolution === DateType.CUSTOM
              ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![0]).startTime
              : getDefaultTime(timeResolution!, displayValue).startTime,
          endTime:
            timeResolution === DateType.CUSTOM
              ? getDefaultTime(customerRequest!.timeResolution, displayValueRange![1]).endTime
              : getDefaultTime(timeResolution!, displayValue).endTime,
        },
      });
    } else {
      return {};
    }
  }, [data, xLabel, ePriceTimes]);

  return (
    <Modal
      title={`详情`}
      size="big"
      contentClassName="modal-form-content"
      open={true}
      onCancel={onCancel}
      footer={[
        <Button key={'cancel'} onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className={styles.filter_box}>
          <Radio.Group
            buttonStyle="solid"
            value={radioValue}
            style={{ marginBottom: 24 }}
            onChange={e => {
              setRadioValue(e.target.value);
            }}
          >
            <Radio.Button value={RankMethodType.ALL}>用量</Radio.Button>
          </Radio.Group>
          <Form.Item label="时间范围">
            {timeResolution === DateType.CUSTOM ? (
              <RangePicker
                picker={picker}
                style={{ width: '100%' }}
                value={displayValueRange}
                disabledDate={disabledDate}
                onChange={dates => onChangePicker(dates)}
                allowClear={false}
              />
            ) : (
              <DatePicker
                style={{ width: '250px' }}
                picker={picker}
                value={displayValue}
                disabledDate={disabledDate}
                onChange={value => {
                  setDisplayValue(value);
                  setDataQueryPeriodId?.(undefined);
                  setDataQueryPeriodName?.(undefined);
                }}
                allowClear={false}
              />
            )}
          </Form.Item>
          {timeResolution !== DateType.DAY && (
            <Form.Item label="数据查询时段">
              <Select
                placeholder="请选择"
                style={{ width: 200 }}
                value={dataQueryPeriodId}
                onChange={(value: number, option) => {
                  setDataQueryPeriodId(value);
                  setDataQueryPeriodName((option as unknown as any).label);
                }}
                onClear={() => {
                  setDataQueryPeriodId(undefined);
                  setDataQueryPeriodName(undefined);
                }}
                options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
                allowClear
              />
            </Form.Item>
          )}
        </div>
        {data?.energyMediumId === 2030 &&
          (timeResolution === DateType.DAY || customerRequest?.timeResolution === DateType.DAY) && (
            <ElectricCard baseInfo={data?.electricRateCardVo} physicalUnitName={data?.physicalUnitName} />
          )}
        {!data?.chartValue ? (
          <Empty className={styles.modal_empty_box} />
        ) : (
          <EchartsWithTheme
            theme={theme}
            style={{ width: '100%', height: 450 }}
            option={chartOption}
            ref={d => {
              if (d) {
                chartRef.current = d;
              }
            }}
            onEvents={{
              brushSelected: (params: any) => {
                let startTime;
                let endTime;
                let total = 0; // 总用量
                const brushComponent = params.batch[0];
                const chartObject = chartRef.current?.getInstance();
                const option = chartObject?.getOption();
                const series = option?.series as any[];

                const rich: any = {};
                const lines: string[] = [];

                brushComponent.selected.forEach((item: any, idx: number) => {
                  // 跳过均值
                  if (item.seriesName === '均值') {
                    return;
                  }
                  const findSeries = series?.find(i => i.name === item.seriesName);

                  let arr: any[] = [];
                  let rate: any[] = [];
                  item.dataIndex.forEach((c: number, index: number) => {
                    if (index === 0) {
                      startTime = dayjs(findSeries.data[c][0], 'x').format(xLabel);
                    }
                    if (index === item.dataIndex.length - 1) {
                      endTime = dayjs(findSeries.data[c][0], 'x').format(xLabel);
                    }
                    arr.push(findSeries.data[c][1]);
                    rate.push(findSeries.data[c]?.[3]);
                  });

                  // 当前系列总和
                  const subtotal = arr.filter(x => !isNil(x)).reduce((a, b) => Number(a) + Number(b), 0);

                  const subRate = rate.filter(x => !isNil(x)).reduce((a, b) => Number(a) + Number(b), 0);
                  total += Number(subtotal);

                  let color: string;

                  // 如果 series 自己有颜色
                  if (findSeries?.itemStyle?.color) {
                    color = findSeries.itemStyle.color as string;
                  } else {
                    const colors = Array.isArray(option!.color) ? option!.color : [option!.color];
                    color = colors[item.seriesIndex % colors.length] as string;
                  }

                  // 定义 rich 样式
                  const markerKey = `m${item.seriesIndex}`;
                  rich[markerKey] = {
                    height: 10,
                    width: 10,
                    backgroundColor: color,
                    borderRadius: 5,
                  };

                  if (!(data?.energyMediumId === 2030 && timeResolution === DateType.DAY)) {
                    // 拼接带 marker 的文本
                    lines.push(
                      `{${markerKey}|} ${item.seriesName}：${keepTwoNull(subtotal)} ${data.physicalUnitName ?? '--'} ${
                        data?.energyMediumId === 2030 && timeResolution !== DateType.DAY
                          ? `占比：${keepPercentOrLine(subRate / arr.length)}%`
                          : ''
                      }`
                    );
                  }
                });

                if (brushComponent.areas.length > 0) {
                  const chartWidth = chartObject?.getWidth?.() || 0;
                  let left = brushComponent.areas[0].range[1] - 240; // 你原来的定位方式
                  // 防止 title 超出右边界
                  if (left + 240 > chartWidth) {
                    left = chartWidth - 240 - 10; // 240是title宽度，10是边距
                  }
                  if (left < 0) {
                    left = 0; // 防止超出左边界
                  }

                  chartObject?.setOption({
                    title: {
                      show: true,
                      padding: 8,
                      backgroundColor: theme === Themes.DARK ? 'rgba(0,0,0,0.8)' : '#fff',
                      borderColor: 'transparent',
                      borderRadius: 2,
                      textStyle: {
                        color: theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 20,
                        rich,
                      },
                      text: [
                        `${startTime} ~ ${endTime} ${dataQueryPeriodName ? dataQueryPeriodName + '总用量' : ''}`,
                        `总用量：${keepTwoNull(total)} ${data.physicalUnitName ?? '--'}`,
                        ...lines,
                      ].join('\n'),
                      top: 100,
                      left,
                      zlevel: 99999,
                    },
                  });
                } else {
                  chartObject?.setOption({
                    title: {
                      show: false,
                    },
                  });
                }
              },
            }}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default ShowDetailModal;
